import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { useToast } from '../../hooks/useToast.js';
import {
  CATEGORIES,
  UNITS,
  type Category,
  type Ingredient,
  type Unit,
} from '../../api/types.js';
import {
  createIngredient,
  updateIngredient,
} from '../../api/ingredients.js';

export interface IngredientDialogProps {
  open: boolean;
  onClose: () => void;
  initialName?: string;
  editing?: Ingredient | null;
  onSaved: (ingredient: Ingredient) => void;
}

const CATEGORY_LABEL: Record<Category, string> = {
  Produce: 'Produce',
  Meat: 'Meat',
  Dairy: 'Dairy',
  'Dry Goods': 'Dry Goods',
  Spices: 'Spices',
  Beverages: 'Beverages',
  Other: 'Other',
};

export function IngredientDialog({
  open,
  onClose,
  initialName,
  editing,
  onSaved,
}: IngredientDialogProps): ReactElement | null {
  const toast = useToast();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<Category>('Other');
  const [defaultUnit, setDefaultUnit] = useState<Unit>('g');
  const [kcal, setKcal] = useState('0');
  const [protein, setProtein] = useState('0');
  const [carbs, setCarbs] = useState('0');
  const [fat, setFat] = useState('0');
  const [weight, setWeight] = useState('');
  const [shoppable, setShoppable] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setCategory(editing.category);
      setDefaultUnit(editing.default_unit);
      setKcal(String(editing.kcal_per_100));
      setProtein(String(editing.protein_per_100));
      setCarbs(String(editing.carbs_per_100));
      setFat(String(editing.fat_per_100));
      setWeight(
        editing.default_unit_weight_g === null
          ? ''
          : String(editing.default_unit_weight_g),
      );
      setShoppable(editing.shoppable);
    } else {
      setName(initialName ?? '');
      setCategory('Other');
      setDefaultUnit('g');
      setKcal('0');
      setProtein('0');
      setCarbs('0');
      setFat('0');
      setWeight('');
      setShoppable(true);
    }
  }, [open, editing, initialName]);

  function num(v: string, fallback = 0): number {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function parseRequired(label: string, raw: string): number | null {
    if (raw.trim() === '') {
      toast.push('error', `${label} is required`);
      return null;
    }
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n)) {
      toast.push('error', `${label} must be a number`);
      return null;
    }
    if (n < 0) {
      toast.push('error', `${label} must be ≥ 0`);
      return null;
    }
    return n;
  }

  async function onSubmit(): Promise<void> {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      toast.push('error', 'Name is required');
      return;
    }
    const kcalVal = parseRequired('kcal per 100g', kcal);
    if (kcalVal === null) return;
    const proteinVal = parseRequired('protein per 100g', protein);
    if (proteinVal === null) return;
    const carbsVal = parseRequired('carbs per 100g', carbs);
    if (carbsVal === null) return;
    const fatVal = parseRequired('fat per 100g', fat);
    if (fatVal === null) return;
    setSubmitting(true);
    try {
      const payload = {
        name: trimmed,
        category,
        default_unit: defaultUnit,
        default_unit_weight_g: weight.trim() === '' ? null : num(weight),
        kcal_per_100: kcalVal,
        protein_per_100: proteinVal,
        carbs_per_100: carbsVal,
        fat_per_100: fatVal,
        shoppable,
      };
      const saved = editing
        ? await updateIngredient(editing.id, payload)
        : await createIngredient(payload);
      onSaved(saved);
      toast.push('success', editing ? 'Ingredient updated' : 'Ingredient created');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.push('error', msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const inputStyle = {
    background: 'var(--bg-primary, transparent)',
    borderColor: 'var(--border)',
    color: 'var(--text-main)',
  };

  return (
    <Dialog open={open} onClose={onClose} title={editing ? 'Edit Ingredient' : 'New Ingredient'}>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
            data-testid="ingredient-name"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Default unit</span>
          <select
            value={defaultUnit}
            onChange={(e) => setDefaultUnit(e.target.value as Unit)}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>kcal / 100g</span>
            <input
              type="number"
              min="0"
              value={kcal}
              onChange={(e) => setKcal(e.target.value)}
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
              data-testid="ingredient-kcal"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Protein g / 100g</span>
            <input
              type="number"
              min="0"
              value={protein}
              onChange={(e) => setProtein(e.target.value)}
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Carbs g / 100g</span>
            <input
              type="number"
              min="0"
              value={carbs}
              onChange={(e) => setCarbs(e.target.value)}
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--text-muted)' }}>Fat g / 100g</span>
            <input
              type="number"
              min="0"
              value={fat}
              onChange={(e) => setFat(e.target.value)}
              className="rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
            />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>
            Default unit weight g (advanced, used when unit=pcs)
          </span>
          <input
            type="number"
            min="0"
            value={weight}
            placeholder="50"
            onChange={(e) => setWeight(e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={inputStyle}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={shoppable}
            onChange={(e) => setShoppable(e.target.checked)}
            data-testid="ingredient-shoppable"
          />
          <span style={{ color: 'var(--text-main)' }}>
            Include in shopping list
          </span>
          <span style={{ color: 'var(--text-muted)' }} className="text-xs">
            (uncheck to skip when generating shopping lists)
          </span>
        </label>
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-2 text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
              minHeight: 40,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submitting}
            className="rounded-md border px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              borderColor: 'var(--accent)',
              color: 'var(--bg-primary, #fff)',
              minHeight: 40,
              opacity: submitting ? 0.6 : 1,
            }}
            data-testid="ingredient-save"
          >
            💾 Save
          </button>
        </div>
      </div>
    </Dialog>
  );
}